/**
 * Screenshots of the visible panes, drawn by the page itself (RFC 0304 3d).
 *
 * A browser cannot photograph its own window, so each pane is re-rendered from
 * its elements. An app pane is a sandboxed frame this page cannot read, so the
 * frame is asked to draw itself over the app bridge and its picture is painted
 * into the pane's. Works the same in the browser and the desktop app.
 */

import { domToCanvas } from 'modern-screenshot'
import SNAPSHOT_RUNTIME from 'modern-screenshot/dist/index.js?raw'

export interface PaneCapture {
  id: string
  title?: string
}

const FRAME_TIMEOUT_MS = 2_000
const MAX_SCALE = 2

export function paneFileName(title: string | undefined, index: number): string {
  const safe = (title ?? `pane-${index + 1}`)
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
  return `view-${safe || `pane-${index + 1}`}.png`
}

const TRANSPARENT = new Set(['', 'transparent', 'rgba(0, 0, 0, 0)'])

/** The colour behind an element: its own, or the nearest ancestor's. */
function backgroundOf(element: HTMLElement): string {
  for (
    let node: HTMLElement | null = element;
    node;
    node = node.parentElement
  ) {
    const color = getComputedStyle(node).backgroundColor
    if (!TRANSPARENT.has(color)) return color
  }
  return '#ffffff'
}

/**
 * Copy live form state onto attributes for the length of a drawing.
 *
 * The drawing clones elements, and a clone keeps attributes but not what the
 * person picked or typed, so a dropdown would show its first option. Returns
 * the undo.
 */
export function pinFormState(root: ParentNode): () => void {
  const undo: Array<() => void> = []
  for (const option of root.querySelectorAll('option')) {
    const had = option.hasAttribute('selected')
    if (option.selected === had) continue
    option.toggleAttribute('selected', option.selected)
    undo.push(() => option.toggleAttribute('selected', had))
  }
  for (const input of root.querySelectorAll('input')) {
    if (input.type === 'checkbox' || input.type === 'radio') {
      const had = input.hasAttribute('checked')
      if (input.checked === had) continue
      input.toggleAttribute('checked', input.checked)
      undo.push(() => input.toggleAttribute('checked', had))
    } else if (input.getAttribute('value') !== input.value) {
      const had = input.getAttribute('value')
      input.setAttribute('value', input.value)
      undo.push(() =>
        had === null
          ? input.removeAttribute('value')
          : input.setAttribute('value', had),
      )
    }
  }
  return () => {
    for (const step of undo) step()
  }
}

/**
 * Ask one app frame to draw itself. Resolves a PNG data URL, or null when the
 * frame errs or does not answer in time; a missing picture is never a failed
 * send.
 */
export function askFrameToDraw(
  frame: HTMLIFrameElement,
  scale: number,
  timeoutMs = FRAME_TIMEOUT_MS,
): Promise<string | null> {
  const target = frame.contentWindow
  if (!target) return Promise.resolve(null)
  const id = `snapshot-${Math.random().toString(36).slice(2)}`
  return new Promise((resolve) => {
    const done = (png: string | null) => {
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      resolve(png)
    }
    const onMessage = (e: MessageEvent) => {
      if (e.source !== target) return
      const d = e.data as { __modBridge?: string; id?: string; png?: unknown }
      if (d?.__modBridge !== 'snapshot-result' || d.id !== id) return
      done(typeof d.png === 'string' ? d.png : null)
    }
    const timer = setTimeout(() => done(null), timeoutMs)
    window.addEventListener('message', onMessage)
    target.postMessage(
      { __modBridge: 'snapshot', id, scale, runtime: SNAPSHOT_RUNTIME },
      '*',
    )
  })
}

async function drawPane(pane: HTMLElement): Promise<Blob | null> {
  const base = pane.getBoundingClientRect()
  if (base.width < 1 || base.height < 1) return null
  const scale = Math.min(window.devicePixelRatio || 1, MAX_SCALE)
  const frames = [
    ...pane.querySelectorAll<HTMLIFrameElement>('iframe[data-mod-app-frame]'),
  ].map((frame) => ({
    rect: frame.getBoundingClientRect(),
    png: askFrameToDraw(frame, scale),
  }))
  const unpin = pinFormState(pane)
  const canvas = await domToCanvas(pane, {
    scale,
    backgroundColor: backgroundOf(pane),
    timeout: 3_000,
    features: { restoreScrollPosition: true },
    filter: (node) => (node as Element).tagName !== 'IFRAME',
  }).finally(unpin)
  const ctx = canvas.getContext('2d')
  for (const frame of frames) {
    const png = await frame.png
    if (!png || !ctx) continue
    const image = new Image()
    image.src = png
    try {
      await image.decode()
    } catch {
      continue
    }
    ctx.drawImage(
      image,
      (frame.rect.left - base.left) * scale,
      (frame.rect.top - base.top) * scale,
      frame.rect.width * scale,
      frame.rect.height * scale,
    )
  }
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}

/** Draw the named panes as they look now. Never throws into send. */
export async function capturePaneImages(
  panes: readonly PaneCapture[],
): Promise<File[]> {
  if (panes.length === 0) return []
  const elements = new Map<string, HTMLElement>()
  for (const node of document.querySelectorAll<HTMLElement>(
    '[data-mod-pane-id]',
  )) {
    const id = node.dataset.modPaneId
    if (id) elements.set(id, node)
  }
  const drawn = await Promise.all(
    panes.map(async (pane, index) => {
      const element = elements.get(pane.id)
      if (!element) return null
      try {
        const blob = await drawPane(element)
        return blob
          ? new File([blob], paneFileName(pane.title, index), {
              type: 'image/png',
            })
          : null
      } catch {
        return null
      }
    }),
  )
  return drawn.filter((file): file is File => file !== null)
}
